import React from 'react'
import styled from 'styled-components'
import { Row } from 'components/common/layout'
import { BaseText } from 'components/common/text'
import { calcDateBefore } from 'utils/displays'
import { FiChevronRight } from 'react-icons/fi'
import { theme } from 'infra/theme'
import { useIsDesktopCSR } from 'utils/hooks'
import {
  DashboardScoutProfileItem,
  DashboardScoutRequestDetail,
  PreInterview,
} from 'types/common'

export enum InterestActionLabelType {
  USER_SAVED_ME = 'userSavedMe',
  USER_VIEWED_ME = 'userViewedMe',
  HAS_CURATION = 'hasCuration',
  STARTUP_SAVED_ME = 'startupSavedMe',
  STARTUP_VIEWED_ME = 'startupViewedMe',
  SAVED_USER = 'savedUser',
  VIEWED_USER = 'viewedUser',
  PRE_INTERVIEW_RESOLVED = 'preInterviewResolved',
  PRE_INTERVIEW_REQUESTED = 'preInterviewRequested',
}

export const getInterestActionLabelType = (
  preInterview: PreInterview | null,
  request?: DashboardScoutProfileItem | DashboardScoutRequestDetail,
) => {
  return preInterview?.requestedAt && preInterview.resolvedAt // 대신 검증 완료
    ? InterestActionLabelType.PRE_INTERVIEW_RESOLVED
    : preInterview?.requestedAt && !preInterview.failedAt // 대신 검증 신청 후 대기 중
    ? InterestActionLabelType.PRE_INTERVIEW_REQUESTED
    : request?.lastListedAt
    ? InterestActionLabelType.SAVED_USER
    : request?.viewedAt
    ? InterestActionLabelType.VIEWED_USER
    : null
}

const labelMapping = (time: string, isDesktop: boolean) => ({
  userSavedMe: {
    description: '🙌  우리 기업을 저장했어요',
    type: 'primary',
    hasTime: true,
  },
  userViewedMe: {
    description: '👀  우리 기업을 확인했어요',
    type: 'default',
    hasTime: true,
  },
  startupSavedMe: {
    description: '🙌  내 프로필을 저장했어요',
    type: 'primary',
    hasTime: true,
  },
  hasCuration: {
    description: '🥇 내가 적합한 인재로 추천받았던 공고예요',
    type: 'secondary',
    hasTime: false,
  },
  startupViewedMe: {
    description: '👀  내 프로필을 확인했어요',
    type: 'default',
    hasTime: true,
  },
  savedUser: {
    description: '🤩  내가 저장한 인재예요!',
    type: 'primary',
    hasTime: false,
  },
  viewedUser: {
    description: `✅ 내가 ${calcDateBefore(time || '')}에 확인한 인재예요`,
    type: 'default',
    hasTime: false,
  },
  preInterviewResolved: {
    description: `✅ 대신 검증 인터뷰가 완료되었어요`,
    type: 'secondary',
    hasTime: false,
  },
  preInterviewRequested: {
    description: isDesktop
      ? `👩‍💻 그룹바이가 대신 인터뷰하고 5일 안에 결과를 전달드려요`
      : `👩‍💻 대신 검증 결과를 5일 안에 전달드려요`,
    type: 'default',
    hasTime: false,
  },
})

export const InterestActionLabel: React.FC<{
  actionType: InterestActionLabelType
  time: string
  labelStyle?: React.CSSProperties
  onClick?: () => void
}> = ({ actionType, time, labelStyle, onClick }) => {
  const isDesktop = useIsDesktopCSR()
  const mapping = labelMapping(time, isDesktop)

  const hasCuration = actionType === InterestActionLabelType.HAS_CURATION
  return (
    <LabelCard
      type={mapping[actionType].type}
      style={{
        ...labelStyle,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <InformText type={mapping[actionType].type}>
        {mapping[actionType].description}
      </InformText>
      {time && mapping[actionType].hasTime && (
        <TimeText type={mapping[actionType].type}>
          {calcDateBefore(time)}
        </TimeText>
      )}
      {hasCuration && (
        <Row style={{ gap: 4 }}>
          {isDesktop && (
            <InformText type='secondary'>추천받은 이유 자세히 보기</InformText>
          )}
          <FiChevronRight size={20} style={{ color: theme.color.white1 }} />
        </Row>
      )}
    </LabelCard>
  )
}

const LabelCard = styled(Row)<{ type: string }>`
  position: relative;
  padding: 8px 24px;
  justify-content: space-between;
  background: ${(p) =>
    p.type === 'primary'
      ? p.theme.color.linear
      : p.type === 'secondary'
      ? p.theme.color.secondary
      : p.theme.color.white1};
  border-bottom: 1px solid ${(p) => p.theme.color.line};
  border-radius: 8px 8px 0 0;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 8px 16px;
  }
`

const TimeText = styled(BaseText)<{ type: string }>`
  color: ${(p) =>
    p.type === 'primary' || p.type === 'secondary'
      ? p.theme.color.white1
      : p.theme.color.gray2};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`

const InformText = styled(TimeText)`
  @media ${(p) => p.theme.deviceSize.desktop} {
    font-weight: ${(p) => p.theme.fontWeight.bold};
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-weight: ${(p) => p.theme.fontWeight.semiBold};
  }
`
